import BreadcrumbItem from 'Components/Atoms/BreadcrumbItem/BreadcrumbItem';
import Container from 'Components/Atoms/Container/Container';
import Dropdown from 'Components/Atoms/Dropdown/Dropdown';
import EntityIcon from 'Components/Atoms/EntityIcon/EntityIcon';
import Grid from 'Components/Atoms/Grid/Grid';
import StatusLabel from 'Components/Atoms/Label/StatusLabel/StatusLabel';
import Loader from 'Components/Atoms/Loader/Loader';
import PageLoader from 'Components/Atoms/Loader/PageLoader/PageLoader';
import Title from 'Components/Atoms/Title/Title';
import Breadcrumbs from 'Components/Molecules/Breadcrumbs/Breadcrumbs';
import PageHeader from 'Components/Organisms/PageHeader/PageHeader';
import React, {Suspense, useEffect, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import {useUserMetadata} from 'Services/Authentication/Auth0/Auth0Service';
import {ROUTES} from 'Services/Route/Route.types';
import EntityHelper from 'Services/Settings/Entity/EntityHelper';
import {SUBSTATUS} from 'Services/Status/Substatus.types';
import {fetchBusinessDataset} from 'Store/Business/Actions';
import {fetchBusinessUnitDataset, fetchBusinessUnitOverviewByPagenumber} from 'Store/BusinessUnit/Actions';
import {fetchCandidateDataset, fetchCandidateOverviewByPagenumber} from 'Store/Candidate/Actions';
import {fetchContactDataset, fetchContactOverviewByPagenumber} from 'Store/Contact/Actions';
import {fetchAllDatasets} from 'Store/FieldManagement/Actions';
import {getRootDatasets} from 'Store/FieldManagement/Selectors';
import {StoreState} from 'Store/Reducers';
import {fetchEntities} from 'Store/Settings/Entities/Actions';
import {getEntityByName} from 'Store/Settings/Entities/Selectors';
import {fetchEntityTypesByEntityId} from 'Store/Settings/EntityTypes/Actions';
import {fetchFields} from 'Store/Settings/Fields/Actions';
import {getFieldsById, isFieldsLoading} from 'Store/Settings/Fields/Selectors';
import {fetchLayouts} from 'Store/Settings/Layout/Actions';
import {
    isEntityFetchingByEntityName,
    isEntityFilteringByEntityName,
    selectEntityPaginationByEntityName,
    selectEntityStatisticsByEntityName
} from 'Store/Shared/Store/Values/Selector';
import {fetchUsersDataset} from 'Store/User/Actions';
import {fetchVacancyDataset, fetchVacancyOverviewByPagenumber} from 'Store/Vacancy/Actions';
import styles from './EntityOverviewPage.module.sass';
import {fetchOfferOverviewByPagenumber} from 'Store/Offer/Actions';
import EntityOverviewHeader from './EntityOverviewHeader/EntityOverviewHeader';
import { TableProvider } from 'Services/Table/TableContext';
import EntityOverviewTable
    from 'Components/Organisms/Entity/EntityOverviewPage/EntityOverviewTable/EntityOverviewTable';

const StatusList = React.lazy(() => import('Components/Molecules/StatusList/StatusList'));

const EntityOverviewPage = ({
    entity_name,

    isFetching, isFiltering,

    entity,
    isFieldsLoading, fieldsById, datasetKeys,
    pagination, statistics,

    ...props
}: EntityOverviewPageProps) => {
    const user_metadata = useUserMetadata();
    const fetchOverviewByPagenumber = useMemo(() => EntityHelper.fetchOverviewByPagenumber(props, entity_name), [entity_name]);
    const [filters, setFilters] = useState(JSON.parse(localStorage?.getItem(`${entity?.name}_filters`) ?? '{}') ?? {});

    const fetchPage = async (filters) => {
        let rawFilters = filters;
        if (Object.keys(rawFilters).length === 0 && localStorage?.getItem(`${entity?.name}_filters`)) {
            rawFilters = JSON.parse(localStorage?.getItem(`${entity?.name}_filters`) ?? '{}');
            setFilters(rawFilters);
        }

        let finalFilters = {};
        Object.keys(rawFilters)
            .forEach(i =>
                finalFilters[encodeURIComponent(i)] = rawFilters[i]
            );

        fetchOverviewByPagenumber(user_metadata?.overview?.[entity?.name] ?? [], 1, finalFilters, true);
        // fetchOverviewByPagenumber(user_metadata?.overview?.[entity?.name] ?? [], 2, finalFilters, true);
        // fetchOverviewByPagenumber(user_metadata?.overview?.[entity?.name] ?? [], 3, finalFilters, true);
    }

    const handleFilterChange = (data) => {
        localStorage?.setItem(`${entity.name}_filters`, JSON.stringify(data));
        setFilters(data);
        fetchPage(data);
    }

    const handleRefresh = () => fetchPage(filters);

    // useEffect(() => {
    //     if (user_metadata?.overview?.[entity?.name]) fetchPage(filters);
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [user_metadata?.overview?.[entity?.name]]);

    useEffect(() => {
        props.fetchEntities();
        props.fetchFields();
        props.fetchLayouts();
        props.fetchAllDatasets();
    }, []);

    useEffect(() => {
        if (entity?.id) props.fetchEntityTypesByEntityId(entity?.id);
        if (entity?.name) fetchPage(filters);
    }, [entity]);

    if (!entity?.id) return <PageLoader description='Enteiten laden...' />;

    if (Object.keys(fieldsById).length === 0) return <PageLoader description='Velden laden...' />;

    if (!statistics?.total?.records) return <PageLoader description={`${EntityHelper.getDisplayValueByName(entity.name)} laden...`} />;

    return <Grid gap='none'>
        <PageHeader
            breadcrumbs={
                <Breadcrumbs>
                    <BreadcrumbItem size='small' path={ROUTES.DASHBOARD.path}>Dashboard</BreadcrumbItem>
                    <BreadcrumbItem size='small'>{ EntityHelper.getDisplayValueByName(entity.name) }</BreadcrumbItem>
                </Breadcrumbs>
            }
        >
            <div className={styles['header']}>
                <div className={styles['header__icon']}>
                    <EntityIcon
                        entity={entity.name}
                        size='xlarge'
                    />
                </div>
                <div className={styles['header__info']}>
                    <Title
                        size='large'
                        weight='bold'
                    >
                        { statistics?.total?.records } { EntityHelper.getDisplayValueByName(entity.name) }
                    </Title>
                    <div className={styles['header__info-status']}>
                        <Dropdown
                            triggerAction='hover'
                            trigger={
                                <StatusLabel status='active'>
                                    { statistics?.total?.ACTIVE ?? 0 } Actief
                                </StatusLabel>
                            }
                        >
                            <Suspense
                                fallback={
                                    <Loader />
                                }
                            >
                                <StatusList
                                    list={
                                        SUBSTATUS
                                        .getListByEntityNameByStatus(entity.name, 'ACTIVE')
                                        ?.map(i => ({
                                            status: 'ACTIVE',
                                            substatus: i,
                                            total: statistics
                                                ?.status
                                                ?.ACTIVE
                                                ?.[i] ?? 0
                                        }))
                                    }
                                />
                            </Suspense>
                        </Dropdown>
                        <Dropdown
                            triggerAction='hover'
                            trigger={
                                <StatusLabel status='passive'>
                                    { statistics?.total?.PASSIVE ?? 0 } Passief
                                </StatusLabel>
                            }
                        >
                            <Suspense fallback={
                                <Loader />
                            }>
                                <StatusList
                                    list={
                                        SUBSTATUS
                                        .getListByEntityNameByStatus(entity.name, 'PASSIVE')
                                        ?.map(i => ({
                                            status: 'PASSIVE',
                                            substatus: i,
                                            total: statistics
                                                ?.status
                                                ?.PASSIVE
                                                ?.[i] ?? 0
                                        }))
                                    }
                                />
                            </Suspense>
                        </Dropdown>
                        <Dropdown
                            triggerAction='hover'
                            trigger={
                                <StatusLabel status='inactive'>
                                    { statistics?.total?.INACTIVE ?? 0 } Inactief
                                </StatusLabel>
                            }
                        >
                            <Suspense fallback={
                                <Loader />
                            }>
                                <StatusList
                                    list={
                                        SUBSTATUS
                                        .getListByEntityNameByStatus(entity.name, 'INACTIVE')
                                        ?.map(i => ({
                                            status: 'INACTIVE',
                                            substatus: i,
                                            total: statistics
                                                ?.status
                                                ?.INACTIVE
                                                ?.[i] ?? 0
                                        }))
                                    }
                                />
                            </Suspense>
                        </Dropdown>
                    </div>
                </div>
            </div>
        </PageHeader>

        <Container>
            <EntityOverviewHeader
                entity_id={entity.id}
                filters={filters}
                onFilterChange={handleFilterChange}
                onRefresh={handleRefresh}
            />
            
            <TableProvider>
                <EntityOverviewTable
                    entity_id={entity.id}
                    filters={filters}
                />
            </TableProvider>
        </Container>
    </Grid>;
}

interface Props {
    entity_name: 'vacancy' | 'candidate' | 'business_unit' | 'contact' | 'offer'
}

const mapState = (state: StoreState, props: Props) => ({
    entity: getEntityByName(state, props.entity_name),

    isFieldsLoading: isFieldsLoading(state),
    fieldsById: getFieldsById(state),

    datasetKeys: getRootDatasets(state),

    pagination: selectEntityPaginationByEntityName(state, props.entity_name),
    statistics: selectEntityStatisticsByEntityName(state, props.entity_name),
    isFetching: isEntityFetchingByEntityName(state, props.entity_name),
    isFiltering: isEntityFilteringByEntityName(state, props.entity_name)
});

const mapDispatch = {
    fetchEntities,
    fetchEntityTypesByEntityId,

    fetchFields,
    fetchLayouts,
    
    fetchVacancyOverviewByPagenumber,
    fetchCandidateOverviewByPagenumber,
    fetchBusinessUnitOverviewByPagenumber,
    fetchContactOverviewByPagenumber,
    fetchOfferOverviewByPagenumber,

    fetchAllDatasets,
    fetchCandidateDataset,
    fetchVacancyDataset,
    fetchContactDataset,
    fetchBusinessDataset,
    fetchBusinessUnitDataset,
    fetchUsersDataset
};

type EntityOverviewPageProps = Props & ReturnType<typeof mapState> & typeof mapDispatch;

export default connect(mapState, mapDispatch)(EntityOverviewPage);